/**
 * Shared card wrapper for goal-related fields in encounter forms.
 * Provides consistent purple-tinted grouping for active-goal progress
 * and new-goal creation steps.
 *
 * @agentic connections
 */

import { ActionIcon, Badge, Group, Paper, Stack, Text } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";

/**
 * Props for {@link EncounterGoalCard}.
 *
 * @agentic connections
 */
export type EncounterGoalCardProps = {
  title: string;
  category?: string;
  children: ReactNode;
  onRemove?: () => void;
  removeAriaLabel?: string;
  hasError?: boolean;
  disabled?: boolean;
};

/**
 * Light purple card grouping goal fields in encounter forms.
 *
 * @agentic connections
 */
export const EncounterGoalCard = ({
  title,
  category,
  children,
  onRemove,
  removeAriaLabel,
  hasError = false,
  disabled = false,
}: EncounterGoalCardProps) => {
  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      bg="var(--mantine-color-purple-0)"
      style={{
        borderColor: hasError
          ? "var(--mantine-color-red-4)"
          : "var(--mantine-color-purple-2)",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : undefined,
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={500} size="sm" lineClamp={2}>
              {title}
            </Text>
            {category && (
              <Badge variant="light" color="gray" size="sm" w="fit-content">
                {category}
              </Badge>
            )}
          </Stack>
          {onRemove && (
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={onRemove}
              disabled={disabled}
              aria-label={removeAriaLabel ?? "Remove goal"}
            >
              <IconTrash size={16} />
            </ActionIcon>
          )}
        </Group>
        {children}
      </Stack>
    </Paper>
  );
};
